import {view, Icon, Spinner} from '@sail/ui';
import {checkCircleFilled, cancelCircleFilled} from '@sail/icons/react/Icon';
import {Text} from 'src/v2/components/common/Text';
import {COLORS} from 'src/v2/util/color';

export type StepStatus = 'completed' | 'inProgress' | 'pending' | 'failed';

type ProgressStep = {
  label: string;
  status: StepStatus;
};

type StepProgressIndicatorProps = {
  steps: ProgressStep[];
};

const CIRCLE_SIZE = '20px';

const StepCircle = ({status, index}: {status: StepStatus; index: number}) => {
  if (status === 'completed') {
    return (
      <Icon
        icon={checkCircleFilled}
        css={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          color: COLORS.textPrimary,
          flexShrink: 0,
        }}
      />
    );
  }

  if (status === 'failed') {
    return (
      <Icon
        icon={cancelCircleFilled}
        css={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          color: COLORS.communicationCritical,
          flexShrink: 0,
        }}
      />
    );
  }

  if (status === 'inProgress') {
    return (
      <view.div
        css={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          backgroundColor: COLORS.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Spinner
          css={{width: 'space.150', height: 'space.150', color: 'white'}}
        />
      </view.div>
    );
  }

  return (
    <view.div
      css={{
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        borderRadius: '50%',
        backgroundColor: COLORS.backgroundNeutral,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Text type="captionEmphasized">{index + 1}</Text>
    </view.div>
  );
};

export const StepProgressIndicator = ({steps}: StepProgressIndicatorProps) => {
  return (
    <view.div css={{display: 'flex', flexDirection: 'column', gap: 'space.0'}}>
      {steps.map((step, i) => (
        <view.div
          key={step.label}
          css={{stack: 'x', gap: 'space.150', alignY: 'top'}}
        >
          <view.div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'space.0',
              alignItems: 'center',
              width: CIRCLE_SIZE,
              flexShrink: 0,
            }}
          >
            <StepCircle status={step.status} index={i} />
            {i < steps.length - 1 ? (
              <view.div
                css={{
                  width: 'space.1',
                  flex: 1,
                  minHeight: 'large',
                  backgroundColor:
                    step.status === 'completed'
                      ? COLORS.textPrimary
                      : COLORS.backgroundNeutral,
                }}
              />
            ) : null}
          </view.div>
          <view.div
            css={{display: 'flex', alignItems: 'center', height: CIRCLE_SIZE}}
          >
            <Text
              type="body"
              css={{
                color:
                  step.status === 'pending'
                    ? COLORS.textDisabled
                    : COLORS.textPrimary,
              }}
            >
              {step.label}
            </Text>
          </view.div>
        </view.div>
      ))}
    </view.div>
  );
};
